import { useEffect, useMemo, useRef } from 'react';
import { GEOLOCATION_ASK_TIMEOUT, GEOLOCATION_PERMISSION } from '../../utils/constants';
import { useDispatch, useSelector } from 'react-redux';
import { clearGps, setGps } from '../../store/reducers/gps';
import useGoogleMapsLoader from '../../hook/useGoogleMapsLoader';
import { detectPlatformInfo, getCurrentLocation, getDetailLocationFromLatLong, isEmptyData } from '../../utils/helper';
import { setUser } from '../../store/reducers/user';

const useCurrentLocation = () => {
	const { isLoaded: isLoadedGoogleMaps } = useGoogleMapsLoader();

	const dispatch = useDispatch();

	const queryParams = new URLSearchParams(window.location.search);
	const latitude = queryParams.get('latitude');
	const longitude = queryParams.get('longitude');

	const { savedData: savedArress } = useSelector((store) => store?.location);

	const { permission, coords } = useSelector((store) => store?.gps);

	const mountedRef = useRef(true);
	const statusRef = useRef(null);

	const normalizePermission = (value) => {
		switch (value) {
			case GEOLOCATION_PERMISSION.GRANTED:
			case GEOLOCATION_PERMISSION.DENIED:
			case GEOLOCATION_PERMISSION.PROMPT:
				return value;
			default:
				return GEOLOCATION_PERMISSION.UNKNOWN;
		}
	};

	const askUserPermission = () => {
		return new Promise((resolve) => {
			let done = false;

			const timeout = setTimeout(() => {
				if (!done) {
					done = true;
					resolve(GEOLOCATION_PERMISSION.UNKNOWN);
				}
			}, GEOLOCATION_ASK_TIMEOUT);

			navigator.geolocation.getCurrentPosition(
				() => {
					if (done) return;
					done = true;
					clearTimeout(timeout);
					resolve(GEOLOCATION_PERMISSION.GRANTED);
				},
				(error) => {
					if (done) return;
					done = true;
					clearTimeout(timeout);

					if (error.code === error.PERMISSION_DENIED) {
						resolve(GEOLOCATION_PERMISSION.DENIED);
					} else {
						resolve(GEOLOCATION_PERMISSION.UNKNOWN);
					}
				},
			);
		});
	};

	const checkPermission = async () => {
		if (!mountedRef.current) return;

		try {
			if (navigator.permissions?.query) {
				const status = await navigator.permissions.query({ name: 'geolocation' });

				statusRef.current = status;

				let normalized = normalizePermission(status.state);

				if (normalized === GEOLOCATION_PERMISSION.PROMPT) {
					normalized = await askUserPermission();
				}

				if (!mountedRef.current) return;

				dispatch(setGps({ permission: normalized }));

				status.onchange = () => {
					if (!mountedRef.current) return;

					const next = normalizePermission(status.state);

					dispatch(setGps({ permission: next }));
				};
			} else {
				const fallback = await askUserPermission();

				if (!mountedRef.current) return;

				dispatch(setGps({ permission: fallback }));
			}
		} catch (err) {
			if (!mountedRef.current) return;

			dispatch(setGps({ permission: GEOLOCATION_PERMISSION.UNKNOWN }));
		}
	};

	const fetchLocation = async () => {
		try {
			const loc = await getCurrentLocation();
			const parsedCoords = {
				latitude: loc.latitude,
				longitude: loc.longitude,
				accuracy: loc.accuracy,
				altitude: loc.altitude,
				altitudeAccuracy: loc.altitudeAccuracy,
				heading: loc.heading,
				speed: loc.speed,
			};
			const log = logging({ message: 'Location retrieved successfully' }, parsedCoords);
			dispatch(setGps({ coords: parsedCoords, logs: log }));
		} catch (err) {
			const log = logging(err);
			dispatch(setGps({ logs: log }));
		}
	};

	const logging = (error, location = null) => {
		return {
			code: error?.code ?? 'UNKNOWN',
			message: error?.message ?? 'TIMEOUT',
			isLoadedGoogleMaps,
			permission: permission,
			coords: location ?? null,
			deviceInfo: navigator.userAgent,
			platformInfo: detectPlatformInfo(navigator.userAgent),
			timestamp: new Date().toISOString(),
		};
	};

	useEffect(() => {
		dispatch(clearGps());
	}, []);

	useEffect(() => {
		mountedRef.current = true;
		if (permission === GEOLOCATION_PERMISSION.GRANTED || permission === GEOLOCATION_PERMISSION.DENIED) return;

		checkPermission();

		return () => {
			mountedRef.current = false;

			if (statusRef.current) {
				statusRef.current.onchange = null;
			}
		};
	}, [checkPermission]);

	const handleGetCurrentLocation = async (coords = null) => {
		try {
			if (!coords?.latitude || !coords?.longitude) return;

			const formated = await getDetailLocationFromLatLong(coords.latitude, coords.longitude);
			const address = {
				address: formated?.address,
				latitude: formated?.lat,
				longitude: formated?.lng,
				country: formated?.country,
				state: formated?.state,
				city: formated?.city,
				village: formated?.village,
				district: formated?.district,
				postalCode: formated?.postalCode,
				detail: null,
			};
			dispatch(
				setUser({
					...address,
					location_permission: permission,
				}),
			);
		} catch (err) {
			console.log('Failed to handle current location: ', err);
		}
	};

	const handleGetAddressFromLatLon = async (latitude, longitude) => {
		try {
			const formated = await getDetailLocationFromLatLong(latitude, longitude);
			dispatch(
				setUser({
					address: formated?.address,
					latitude: formated?.lat,
					longitude: formated?.lng,
					country: formated?.country,
					state: formated?.state,
					city: formated?.city,
					village: formated?.village,
					district: formated?.district,
					postalCode: formated?.postalCode,
					detail: null,
				}),
			);
		} catch (error) {
			console.log('error: ', error);
		}
	};

	const handleGetAddressFromSavedAddress = (primaryAddress = null) => {
		try {
			if (!primaryAddress) return;

			const address = {
				address: primaryAddress?.address,
				latitude: primaryAddress?.latitude,
				longitude: primaryAddress?.longitude,
				country: primaryAddress?.country,
				state: primaryAddress?.state,
				city: primaryAddress?.city,
				village: primaryAddress?.village,
				district: primaryAddress?.district,
				postalCode: primaryAddress?.postalCode,
				detail: primaryAddress?.address_detail || null,
			};
			dispatch(
				setUser({
					...address,
				}),
			);
		} catch (error) {
			console.log('error: ', error);
		}
	};

	const selectLocation = useMemo(() => {
		if (permission === GEOLOCATION_PERMISSION.GRANTED && !isEmptyData(coords?.latitude) && !isEmptyData(coords?.longitude)) {
			return {
				type: 'GPS',
				data: coords,
			};
		}
		if (!isEmptyData(latitude) && !isEmptyData(longitude)) {
			return {
				type: 'PARAMS',
				data: {
					latitude,
					longitude,
				},
			};
		}
		const saved = savedArress?.find((address) => address?.primary === '1');
		if (!isEmptyData(saved)) {
			return {
				type: 'SAVED',
				data: saved,
			};
		}
		return {
			type: 'PASS',
			data: null,
		};
	}, [coords, latitude, longitude, savedArress]);

	useEffect(() => {
		if (!isLoadedGoogleMaps || permission !== GEOLOCATION_PERMISSION.GRANTED) return;
		fetchLocation();
	}, [isLoadedGoogleMaps, permission]);

	useEffect(() => {
		if (permission !== GEOLOCATION_PERMISSION.GRANTED) {
			logging({ message: 'Permission not granted' });
		}
	}, [permission]);

	useEffect(() => {
    switch (selectLocation.type) {
			case 'GPS':
				console.log('GPS');
				handleGetCurrentLocation(selectLocation.data);
				break;

			case 'PARAMS':
				console.log('PARAMS');
				handleGetAddressFromLatLon(selectLocation.data.latitude, selectLocation.data.longitude);
				break;

			case 'SAVED':
				console.log('SAVED');
				handleGetAddressFromSavedAddress(selectLocation?.data);
				break;

			default:
		}
	}, [selectLocation]);
};

export default useCurrentLocation;
